<script setup>
// Relatorio de numeros de uma campanha de disparo (v75) — usado pela aba
// Disparos da caixa E pelo Meta -> Historico. Traz filtro por resultado
// (Total/Disparados/Entregues/Lidos/Erros/Pendentes), paginacao, e por linha:
// - Lupa: mostra a mensagem EXATA enviada aquele cliente (variaveis ja
//   substituidas — content gravado pelo disparo);
// - WhatsApp: janela estilo iPhone com a conversa inteira SOMENTE LEITURA
//   (mesmas mensagens de Conversas, rolavel) + botao ABRIR CONVERSA que
//   fecha e navega pra conversa ao vivo.
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxDispatchesAPI from 'dashboard/api/inboxDispatches';
import MessageApi from 'dashboard/api/inbox/message';

const props = defineProps({
  inboxId: {
    type: [Number, String],
    required: true,
  },
  campaign: {
    type: Object,
    required: true,
  },
});

const router = useRouter();

const itemsPage = ref(1);
const itemsData = ref({ total: 0, data: [] });
const isLoadingItems = ref(false);
const resultFilter = ref('');
let pollTimer = null;

const RESULT_FILTER_OPTIONS = [
  { value: '', label: 'Total' },
  { value: 'disparado', label: 'Disparados' },
  { value: 'entregue', label: 'Entregues' },
  { value: 'lido', label: 'Lidos' },
  { value: 'erro', label: 'Erros' },
  { value: 'pendente', label: 'Pendentes' },
];

const RESULT_LABELS = {
  pendente: 'Pendente',
  disparado: 'Disparado ✓',
  entregue: 'Entregue ✓✓',
  lido: 'Lido ✓✓',
  erro: 'Erro',
};

const RESULT_CLASSES = {
  pendente: 'bg-n-slate-3 text-n-slate-12',
  disparado: 'bg-n-teal-3 text-n-teal-12',
  entregue: 'bg-n-teal-3 text-n-teal-12',
  lido: 'bg-n-blue-3 text-n-blue-12',
  erro: 'bg-n-ruby-3 text-n-ruby-12',
};

const totalItemPages = computed(() =>
  Math.max(1, Math.ceil(itemsData.value.total / 10))
);

const fetchItems = async page => {
  isLoadingItems.value = true;
  try {
    const { data } = await InboxDispatchesAPI.listItems(
      props.inboxId,
      props.campaign.id,
      page,
      resultFilter.value
    );
    itemsPage.value = data.page;
    itemsData.value = data;
  } catch (e) {
    // silencioso: o refresh seguinte tenta de novo
  } finally {
    isLoadingItems.value = false;
  }
};

// --- Lupa: mensagem enviada -------------------------------------------------
const previewItem = ref(null);

// --- WhatsApp: conversa no "iphone" ------------------------------------------
const phoneItem = ref(null);
const phoneMessages = ref([]);
const isLoadingPhone = ref(false);
const hasOlder = ref(false);
const phoneBody = ref(null);

const scrollPhoneToBottom = () => {
  nextTick(() => {
    if (phoneBody.value) phoneBody.value.scrollTop = phoneBody.value.scrollHeight;
  });
};

const openPhone = async item => {
  phoneItem.value = item;
  phoneMessages.value = [];
  hasOlder.value = false;
  isLoadingPhone.value = true;
  try {
    const { data } = await MessageApi.getPreviousMessages({
      conversationId: item.conversation_display_id,
    });
    phoneMessages.value = data.payload || [];
    hasOlder.value = phoneMessages.value.length >= 20;
    scrollPhoneToBottom();
  } catch (e) {
    phoneMessages.value = [];
  } finally {
    isLoadingPhone.value = false;
  }
};

const loadOlder = async () => {
  if (!phoneMessages.value.length) return;
  const before = phoneMessages.value[0].id;
  isLoadingPhone.value = true;
  try {
    const { data } = await MessageApi.getPreviousMessages({
      conversationId: phoneItem.value.conversation_display_id,
      before,
    });
    const older = data.payload || [];
    phoneMessages.value = [...older, ...phoneMessages.value];
    hasOlder.value = older.length >= 20;
  } catch (e) {
    hasOlder.value = false;
  } finally {
    isLoadingPhone.value = false;
  }
};

const openConversation = () => {
  const displayId = phoneItem.value?.conversation_display_id;
  phoneItem.value = null;
  if (!displayId) return;
  router.push({
    name: 'inbox_conversation',
    params: { conversation_id: displayId },
  });
};

const bubbleSide = m => (m.message_type === 1 ? 'self-end' : 'self-start');
const bubbleColor = m => {
  if (m.private) return 'bg-amber-700/60 text-white';
  return m.message_type === 1
    ? 'bg-[#005c4b] text-white'
    : 'bg-[#202c33] text-white';
};

const isActivity = m => m.message_type === 2;

const formatTime = ts => {
  if (!ts) return '';
  const d = new Date(ts * 1000);
  return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

const imageAttachments = m =>
  (m.attachments || []).filter(a => a.file_type === 'image');
const otherAttachments = m =>
  (m.attachments || []).filter(a => a.file_type !== 'image');

onMounted(() => {
  fetchItems(1);
  pollTimer = setInterval(() => {
    if (['queued', 'running'].includes(props.campaign.status)) {
      fetchItems(itemsPage.value);
    }
  }, 6000);
});

onUnmounted(() => {
  if (pollTimer) clearInterval(pollTimer);
});
</script>

<template>
  <div>
    <div class="flex items-center justify-end mb-2">
      <select
        v-model="resultFilter"
        class="h-8 rounded-lg border border-n-weak bg-n-alpha-black2 px-2 text-xs text-n-slate-12 outline-none"
        @change="fetchItems(1)"
      >
        <option
          v-for="opt in RESULT_FILTER_OPTIONS"
          :key="opt.value"
          :value="opt.value"
        >
          {{ opt.label }}
        </option>
      </select>
    </div>

    <div
      v-if="isLoadingItems && !itemsData.data.length"
      class="text-xs text-n-slate-11"
    >
      Carregando disparos…
    </div>
    <div
      v-else-if="!itemsData.data.length"
      class="text-xs text-n-slate-11 py-2"
    >
      Nenhum disparo com esse resultado nesta campanha.
    </div>
    <template v-else>
      <table class="w-full text-xs">
        <thead>
          <tr class="text-left text-n-slate-11">
            <th class="px-2 py-1 font-medium">#</th>
            <th class="px-2 py-1 font-medium">Telefone</th>
            <th class="px-2 py-1 font-medium">Nome</th>
            <th class="px-2 py-1 font-medium">Resultado</th>
            <th class="px-2 py-1 font-medium">Erro</th>
            <th class="px-2 py-1 font-medium" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in itemsData.data"
            :key="item.row_number"
            class="border-t border-n-weak"
          >
            <td class="px-2 py-1 text-n-slate-11">{{ item.row_number }}</td>
            <td class="px-2 py-1 font-mono text-n-slate-12">
              {{ item.phone }}
            </td>
            <td class="px-2 py-1 text-n-slate-12">{{ item.name }}</td>
            <td class="px-2 py-1">
              <span
                class="inline-flex px-1.5 py-0.5 rounded font-medium"
                :class="RESULT_CLASSES[item.result]"
              >
                {{ RESULT_LABELS[item.result] || item.result }}
              </span>
            </td>
            <td class="px-2 py-1 text-n-ruby-11">{{ item.error || '—' }}</td>
            <td class="px-2 py-1">
              <div class="flex items-center gap-1 justify-end">
                <NextButton
                  size="xs"
                  variant="ghost"
                  color="slate"
                  icon="i-lucide-search"
                  title="Ver a mensagem enviada"
                  :disabled="!item.message_content"
                  @click="previewItem = item"
                />
                <NextButton
                  size="xs"
                  variant="ghost"
                  color="teal"
                  icon="i-ri-whatsapp-line"
                  title="Ver a conversa"
                  :disabled="!item.conversation_display_id"
                  @click="openPhone(item)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="flex items-center gap-2 mt-2">
        <NextButton
          size="xs"
          variant="outline"
          color="slate"
          label="Anterior"
          :disabled="itemsPage <= 1"
          @click="fetchItems(itemsPage - 1)"
        />
        <span class="text-xs text-n-slate-11">
          Página {{ itemsPage }} de {{ totalItemPages }} ({{
            itemsData.total
          }} disparos)
        </span>
        <NextButton
          size="xs"
          variant="outline"
          color="slate"
          label="Próxima"
          :disabled="itemsPage >= totalItemPages"
          @click="fetchItems(itemsPage + 1)"
        />
      </div>
    </template>

    <!-- ============ LUPA: mensagem enviada ============ -->
    <div
      v-if="previewItem"
      class="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      @click.self="previewItem = null"
    >
      <div class="bg-n-solid-2 rounded-xl p-5 w-full max-w-lg shadow-xl">
        <div class="flex items-start justify-between mb-3">
          <div>
            <h3 class="text-base font-medium text-n-slate-12">
              Mensagem enviada
            </h3>
            <p class="text-xs text-n-slate-11">
              {{ previewItem.name }} — {{ previewItem.phone }}
            </p>
          </div>
          <NextButton
            size="xs"
            variant="ghost"
            color="slate"
            icon="i-lucide-x"
            @click="previewItem = null"
          />
        </div>
        <div class="rounded-lg bg-[#0b141a] p-4">
          <div
            class="bg-[#005c4b] text-white rounded-lg rounded-tr-none px-3 py-2 text-sm whitespace-pre-wrap ml-auto max-w-[90%] w-fit"
          >
            {{ previewItem.message_content }}
          </div>
        </div>
      </div>
    </div>

    <!-- ============ WHATSAPP: conversa no "iphone" ============ -->
    <div
      v-if="phoneItem"
      class="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      @click.self="phoneItem = null"
    >
      <div
        class="w-[380px] max-w-full h-[720px] max-h-[90vh] rounded-[2.5rem] border-[6px] border-black bg-[#0b141a] shadow-2xl flex flex-col overflow-hidden"
      >
        <!-- topo do "telefone" -->
        <div
          class="bg-[#202c33] px-4 pt-5 pb-3 flex items-center justify-between gap-2 shrink-0"
        >
          <div class="min-w-0">
            <p class="text-sm font-medium text-white truncate">
              {{ phoneItem.name || phoneItem.phone }}
            </p>
            <p class="text-[11px] text-n-slate-10 truncate">
              {{ phoneItem.phone }} · somente visualização
            </p>
          </div>
          <div class="flex items-center gap-1 shrink-0">
            <NextButton
              size="xs"
              label="ABRIR CONVERSA"
              @click="openConversation"
            />
            <NextButton
              size="xs"
              variant="ghost"
              color="slate"
              icon="i-lucide-x"
              @click="phoneItem = null"
            />
          </div>
        </div>

        <!-- mensagens -->
        <div
          ref="phoneBody"
          class="flex-1 overflow-y-auto px-3 py-2 flex flex-col gap-1.5"
        >
          <div v-if="hasOlder" class="flex justify-center py-1">
            <NextButton
              size="xs"
              variant="outline"
              color="slate"
              label="Carregar mensagens anteriores"
              :is-loading="isLoadingPhone"
              @click="loadOlder"
            />
          </div>
          <p
            v-if="isLoadingPhone && !phoneMessages.length"
            class="text-center text-xs text-n-slate-10 py-6"
          >
            Carregando conversa…
          </p>
          <p
            v-else-if="!phoneMessages.length"
            class="text-center text-xs text-n-slate-10 py-6"
          >
            Nenhuma mensagem nesta conversa.
          </p>
          <template v-for="m in phoneMessages" :key="m.id">
            <p
              v-if="isActivity(m)"
              class="text-center text-[10px] text-n-slate-10 py-0.5"
            >
              {{ m.content }}
            </p>
            <div
              v-else
              class="max-w-[80%] rounded-lg px-2.5 py-1.5"
              :class="[bubbleSide(m), bubbleColor(m)]"
            >
              <p v-if="m.private" class="text-[10px] opacity-80 mb-0.5">
                🔒 Nota privada
              </p>
              <img
                v-for="att in imageAttachments(m)"
                :key="att.id"
                :src="att.data_url"
                class="rounded-md max-h-48 mb-1"
              />
              <a
                v-for="att in otherAttachments(m)"
                :key="att.id"
                :href="att.data_url"
                target="_blank"
                rel="noopener"
                class="block text-xs underline mb-1"
              >
                📎 Anexo ({{ att.file_type }})
              </a>
              <p
                v-if="m.content"
                class="text-[13px] whitespace-pre-wrap break-words"
              >
                {{ m.content }}
              </p>
              <p class="text-[9px] opacity-60 text-right mt-0.5">
                {{ formatTime(m.created_at) }}
              </p>
            </div>
          </template>
        </div>

        <!-- rodape do "telefone" (sem campo de digitar — so leitura) -->
        <div class="bg-[#202c33] px-4 py-2 shrink-0">
          <p class="text-center text-[10px] text-n-slate-10">
            Visualização somente leitura
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
